import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type UiLocale = 'en' | 'fr' | 'zh' | 'ru';

const STORAGE_KEY = 'llambada_ui_locale';

const TRANSLATIONS: Record<Exclude<UiLocale, 'en'>, Record<string, string>> = {
  fr: {
    'Language': 'Langue',
    'English': 'Anglais',
    'French': 'Français',
    'Chinese': 'Chinois',
    'Russian': 'Russe',
    'Switch to dark mode': 'Passer en mode sombre',
    'Switch to light mode': 'Passer en mode clair',
    'Back': 'Retour',
    'Dashboard': 'Tableau de bord',
    'Sign In': 'Se connecter',
    'Sign out': 'Se déconnecter',
    'Settings': 'Paramètres',
    'Earnings': 'Revenus',
    'Admin': 'Admin',
    'Toggle menu': 'Afficher/masquer le menu',
    'Browse Public Apps': 'Parcourir les apps publiques',
    'Admin Dashboard': 'Tableau de bord admin',
    'Terms': 'Conditions',
    'Privacy': 'Confidentialité',
    'Contact': 'Contact',
    'Documentation': 'Documentation',
    'Creators': 'Créateurs',
    'Pricing': 'Tarifs',
    'Copyright': 'Droits d’auteur',
    'Build your own mini-app': 'Créez votre propre mini-app',
    'Powered by Llambada': 'Propulsé par Llambada',
    'About': 'À propos',
    'Account': 'Compte',
    'Sign in': 'Se connecter',
    'Email address': 'Adresse e-mail',
    'Password': 'Mot de passe',
    'Welcome back': 'Bon retour',
    'Search': 'Rechercher',
    'Refresh': 'Actualiser',
    'Cancel': 'Annuler',
    'Create account': 'Créer un compte',
    'Create an account': 'Créer un compte',
    'Sign up with Google': 'S’inscrire avec Google',
    'Sign in with Email': 'Se connecter avec e-mail',
    'Or sign in with email': 'Ou se connecter avec e-mail',
    'Or sign up with email': 'Ou s’inscrire avec e-mail',
    'Forgot password?': 'Mot de passe oublié ?',
    'Reset your password': 'Réinitialiser votre mot de passe',
    'Back to sign in': 'Retour à la connexion',
    'Send reset link': 'Envoyer le lien de réinitialisation',
    'Update password': 'Mettre à jour le mot de passe',
    'Public Mini-Apps': 'Mini-apps publiques',
    'All mini-apps': 'Toutes les mini-apps',
    'Games': 'Jeux',
    'Utilities': 'Utilitaires',
    'Top Results': 'Meilleurs résultats',
    'Try Again': 'Réessayer',
    'Build Your Mini-App': 'Créez votre mini-app',
    'Yes, build my mini-app': 'Oui, créez ma mini-app',
    'Create mini-app': 'Créer une mini-app',
    'Your Mini-Apps': 'Vos mini-apps',
    'New Mini-App': 'Nouvelle mini-app',
    'No mini-apps yet': 'Aucune mini-app pour le moment',
    'Delete': 'Supprimer',
    'Delete mini-app': 'Supprimer la mini-app',
    'View public page': 'Voir la page publique',
    'Published': 'Publiée',
    'Private': 'Privée',
    'Creator Monetization': 'Monétisation créateur',
    'Subscription': 'Abonnement',
    'Upgrade': 'Passer à une offre supérieure',
    'Manage': 'Gérer',
    'Preferences': 'Préférences',
    'Save Settings': 'Enregistrer les paramètres',
    'Remove All Keys': 'Supprimer toutes les clés',
    'Settings saved successfully': 'Paramètres enregistrés avec succès',
    'Country': 'Pays',
    'Username': "Nom d'utilisateur",
    'Nickname': 'Pseudo',
    'Public profile': 'Profil public',
    'View your public profile': 'Voir votre profil public',
    'Become a Creator': 'Devenir créateur',
    'Get Started Free': 'Commencer gratuitement',
    'How It Works': 'Comment ça marche',
    'Flexible AI Access': 'Accès IA flexible',
    'View Pricing Plans': 'Voir les offres',
    'Ready to Build Your First Mini-App?': 'Prêt à créer votre première mini-app ?',
    'Create Free Account': 'Créer un compte gratuit',
    'Browse Mini-Apps': 'Parcourir les mini-apps',
    "Don't have an account?": "Vous n'avez pas de compte ?",
    "Enter your email and we'll send you a reset link": 'Entrez votre e-mail et nous vous enverrons un lien de réinitialisation',
    "We've sent a password reset link to": 'Nous avons envoyé un lien de réinitialisation à',
    "Didn't receive the email? Check your spam folder or try again.": "Vous n'avez pas reçu l'e-mail ? Vérifiez vos spams ou réessayez.",
    "Google Sign-in is blocked by this app's built-in browser.": "La connexion Google est bloquée par le navigateur intégré de cette application.",
    "Google Sign-up is blocked by this app's built-in browser.": "L'inscription Google est bloquée par le navigateur intégré de cette application.",
  },
  zh: {
    'Language': '语言',
    'English': '英语',
    'French': '法语',
    'Chinese': '中文',
    'Russian': '俄语',
    'Switch to dark mode': '切换到深色模式',
    'Switch to light mode': '切换到浅色模式',
    'Back': '返回',
    'Dashboard': '控制台',
    'Sign In': '登录',
    'Sign out': '退出登录',
    'Settings': '设置',
    'Earnings': '收益',
    'Admin': '管理',
    'Toggle menu': '切换菜单',
    'Browse Public Apps': '浏览公开应用',
    'Admin Dashboard': '管理控制台',
    'Terms': '服务条款',
    'Privacy': '隐私政策',
    'Contact': '联系我们',
    'Documentation': '文档',
    'Creators': '创作者',
    'Pricing': '定价',
    'Copyright': '版权',
    'Build your own mini-app': '创建你自己的迷你应用',
    'Powered by Llambada': '由 Llambada 提供支持',
    'About': '关于',
    'Account': '账户',
    'Account created!': '账户已创建！',
    'All features, highest limits': '全部功能，最高限额',
    'All mini-apps': '全部迷你应用',
    'Allow others to see your profile page and public mini-apps': '允许他人查看你的个人主页和公开迷你应用',
    'Already have an account?': '已经有账户了？',
    'Anthropic API Key': 'Anthropic API 密钥',
    'Are you sure you want to delete this mini-app? This action cannot be undone and all conversations will be permanently lost.': '确定要删除这个迷你应用吗？此操作不可撤销，所有对话将被永久删除。',
    'Auto-submit errors to AI': '自动将错误提交给 AI',
    'Back to sign in': '返回登录',
    'Basic features': '基础功能',
    'Become a Creator': '成为创作者',
    'Bring Your Own API Key': '使用你自己的 API 密钥',
    'Browse Mini-Apps': '浏览迷你应用',
    'Browser Not Supported': '浏览器不受支持',
    'Build Your Mini-App': '创建你的迷你应用',
    'Build and test mini-apps for free': '免费构建并测试迷你应用',
    'Cancel': '取消',
    'Check back later for new game mini-apps.': '稍后再来查看新的游戏类迷你应用。',
    'Check back later for new mini-apps.': '稍后再来查看新的迷你应用。',
    'Check back later for new utility mini-apps.': '稍后再来查看新的工具类迷你应用。',
    'Check your Stripe dashboard to complete any required verification steps': '请在 Stripe 控制台完成所需验证步骤',
    'Check your email': '请检查你的邮箱',
    'Choose a new password for your account': '为你的账户设置新密码',
    'Click on Settings (gear icon)': '点击设置（齿轮图标）',
    'Confirm new password': '确认新密码',
    'Connect Stripe for Payouts': '连接 Stripe 以接收付款',
    'Connect your bank account to receive payouts when users run your mini-apps.': '连接你的银行账户，当用户使用你的迷你应用时接收分成。',
    'Connecting to Google...': '正在连接 Google...',
    'Continue with Google': '使用 Google 继续',
    'Copied!': '已复制！',
    'Copy Page Link': '复制页面链接',
    'Country': '国家',
    'Country is set during registration and cannot be changed': '国家在注册时设置，之后不可更改',
    'Create Free Account': '创建免费账户',
    'Create account': '创建账户',
    'Create an account': '创建账户',
    'Create and manage your LLM mini-apps': '创建并管理你的 LLM 迷你应用',
    'Create mini-app': '创建迷你应用',
    'Create new mini-app': '创建新的迷你应用',
    'Create one': '去创建',
    'Create unlimited private mini-apps and publish one public mini-app (non-monetized)': '可创建无限个私有迷你应用，并发布一个公开迷你应用（不变现）',
    'Create your first mini-app': '创建你的第一个迷你应用',
    'Create your first mini-app to start having conversations with LLMs and organizing your work.': '创建你的第一个迷你应用，开始与 LLM 对话并组织你的工作。',
    'Creating...': '创建中...',
    'Creator Monetization': '创作者变现',
    'Delete': '删除',
    'Delete mini-app': '删除迷你应用',
    'Display name': '显示名称',
    'Email address': '邮箱地址',
    'Enter your Anthropic API key': '输入你的 Anthropic API 密钥',
    'Enter your Anthropic API key to use the coding AI. Get your key from': '输入你的 Anthropic API 密钥以使用编程 AI。获取密钥请访问',
    'Enter your Kimi API key to use the coding AI. Get your key from': '输入你的 Kimi API 密钥以使用编程 AI。获取密钥请访问',
    'Every creator gets limited free access to our code-writing AI. Perfect for getting started, experimenting with ideas, and building your first mini-apps.': '每位创作者都可获得有限的免费代码生成 AI 使用额度，适合起步、尝试想法和构建第一个迷你应用。',
    'Explore and use mini-apps created by our community': '探索并使用社区创作者制作的迷你应用',
    'Failed to create conversation': '创建会话失败',
    'Failed to create mini-app': '创建迷你应用失败',
    'Failed to load mini-apps': '加载迷你应用失败',
    'Flexible AI Access': '灵活的 AI 使用方式',
    'Forgot password?': '忘记密码？',
    'Fork': '分叉',
    'Free AI Credits': '免费 AI 积分',
    'Free accounts are limited to 1 mini-app.': '免费账户最多可创建 1 个迷你应用。',
    'Free users:': '免费用户：',
    'Full features, no ads': '完整功能，无广告',
    'Games': '游戏',
    'Generating title...': '正在生成标题...',
    'Get Started Free': '免费开始',
    'Go to Dashboard': '前往控制台',
    'Go to Sign In': '前往登录',
    'Go to your Dashboard': '前往你的控制台',
    'Got it': '明白了',
    'How It Works': '工作原理',
    'How to add your API key:': '如何添加 API 密钥：',
    'Invalid or expired link': '链接无效或已过期',
    'John Doe': '张三',
    'Join creators who are building useful tools and earning money - all without writing a single line of code.': '加入正在构建实用工具并获得收益的创作者行列，无需手写一行代码。',
    'Kimi API Key': 'Kimi API 密钥',
    'GLM API Key': 'GLM API 密钥',
    'Enter your Z.ai GLM API key to use GLM coding models. Get your key from': '输入您的 Z.ai GLM API 密钥以使用 GLM 编码模型。从以下网站获取密钥',
    'Let our AI build this app for you': '让我们的 AI 为你构建这个应用',
    'Llambada - Create an account': 'Llambada - 创建账户',
    'Llambada - Sign in': 'Llambada - 登录',
    'AI-Powered Mini-App Builder': 'AI 驱动的迷你应用构建器',
    'Llambada provides AI capabilities to help you build, with options that fit your needs.': 'Llambada 提供多种 AI 方案，帮助你按需构建应用。',
    'Manage': '管理',
    'Max Plan Active': 'Max 套餐已启用',
    'Maybe Later': '稍后再说',
    'Mini-app name': '迷你应用名称',
    'Must be at least 6 characters': '至少需要 6 个字符',
    'My awesome mini-app': '我的超棒迷你应用',
    'New Mini-App': '新建迷你应用',
    'New password': '新密码',
    'Nickname': '昵称',
    'No coding experience required. Just describe what you want your mini-app to do, and our AI will build it for you. Share it with the world and earn money.': '无需编程经验。只要描述你希望迷你应用实现的功能，我们的 AI 就会帮你构建。发布给全世界并获得收益。',
    'No credit card required to start': '开始无需信用卡',
    'No mini-apps found matching your search': '未找到与你搜索匹配的迷你应用',
    'No mini-apps yet': '还没有迷你应用',
    'No public games yet': '暂无公开游戏应用',
    'No public mini-apps yet': '暂无公开迷你应用',
    'No public utilities yet': '暂无公开工具应用',
    'Or copy link manually': '或手动复制链接',
    'Or sign in with email': '或使用邮箱登录',
    'Or sign up with email': '或使用邮箱注册',
    'Paid subscribers:': '付费订阅用户：',
    'Password': '密码',
    'Password must be at least 6 characters': '密码至少需要 6 个字符',
    'Password updated!': '密码已更新！',
    'Passwords do not match': '两次输入的密码不一致',
    'Payouts Enabled': '收款已启用',
    'Payouts are processed monthly via Stripe Connect. Track your earnings in real-time from your dashboard.': '收益通过 Stripe Connect 按月结算。你可以在控制台实时查看收益。',
    'Please check your email to verify your account.': '请检查邮箱并验证你的账户。',
    'Please select your country': '请选择你的国家',
    'Power users can connect their own Anthropic API key for unlimited AI usage. You pay Anthropic directly and get full control over your AI costs.': '高级用户可连接自己的 Anthropic API 密钥以实现不限量 AI 使用。你将直接向 Anthropic 付费并完全掌控成本。',
    'Preferences': '偏好设置',
    'Press Enter to search': '按 Enter 搜索',
    'Private': '私有',
    'Public Mini-Apps': '公开迷你应用',
    'Public profile': '公开资料页',
    'Publish multiple mini-apps publicly and earn 70% revenue share': '可公开发布多个迷你应用并获得 70% 收益分成',
    'Published': '已发布',
    'Publishing & Earning Requirements': '发布与收益要求',
    'Ready to Build Your First Mini-App?': '准备好创建你的第一个迷你应用了吗？',
    'Ready to build your new mini-app?': '准备好创建你的新迷你应用了吗？',
    'Redirecting to login...': '正在跳转到登录页...',
    'Refresh': '刷新',
    'Remove All Keys': '移除所有密钥',
    'Request new link': '请求新链接',
    'Reset your password': '重置你的密码',
    'Save Settings': '保存设置',
    'Save and start building unlimited': '保存并开始无限构建',
    'Search': '搜索',
    'Search failed': '搜索失败',
    'Select your country': '选择你的国家',
    'Send reset link': '发送重置链接',
    'Set a username first to make your profile public': '请先设置用户名再公开个人资料',
    'Set new password': '设置新密码',
    'Settings saved successfully': '设置已成功保存',
    'Sign in': '登录',
    'Sign in to continue to your projects': '登录以继续访问你的项目',
    'Sign in with Email': '使用邮箱登录',
    'Sign up with Google': '使用 Google 注册',
    'Simply tell the AI what you want your mini-app to do. Describe the problem it should solve or the task it should help users accomplish. No technical jargon needed.': '只需告诉 AI 你的迷你应用要做什么。描述它要解决的问题或帮助用户完成的任务，不需要技术术语。',
    'Standard features with ads': '标准功能（含广告）',
    'Start Building': '开始构建',
    'Start building your LLM projects today': '今天就开始构建你的 LLM 项目',
    'Stripe Connected - Verification Pending': 'Stripe 已连接 - 等待验证',
    'Subscription': '订阅',
    'Talking to AI': '与 AI 对话',
    'Tap the menu icon (•••) in the corner and select "Open in Browser".': '点击右上角菜单图标（•••），选择“在浏览器中打开”。',
    'This password reset link is invalid or has expired. Please request a new one.': '此密码重置链接无效或已过期，请重新申请。',
    'Top Results': '最佳结果',
    'Try Again': '重试',
    'Try another email': '尝试其他邮箱',
    'Type the app you are looking for, for example: "Fill and sign a PDF form"': '输入你想找的应用，例如：“填写并签署 PDF 表单”',
    'Update password': '更新密码',
    'Upgrade': '升级',
    'Upgrade anytime for more usage': '随时升级以获得更多额度',
    'Upgrade to Create More Mini-Apps': '升级以创建更多迷你应用',
    'Upgrade to the Standard plan to create unlimited mini-apps and unlock more features.': '升级到标准套餐以创建无限迷你应用并解锁更多功能。',
    'Used for payouts. Cannot be changed later.': '用于打款，之后不可更改。',
    'Username': '用户名',
    'Utilities': '工具',
    'Validating reset link...': '正在验证重置链接...',
    'View Plans': '查看套餐',
    'View Pricing Plans': '查看定价套餐',
    'View Stripe Dashboard': '查看 Stripe 控制台',
    'View public page': '查看公开页面',
    'View your public profile': '查看你的公开资料页',
    'Want to build mini-apps like these?': '想构建这样的迷你应用吗？',
    'Welcome back': '欢迎回来',
    'Welcome to {tier}!': '欢迎使用 {tier}！',
    '{count} credits added!': '{count} 个积分已添加！',
    'Your credits have been added to your account.': '积分已添加到你的账户。',
    'When enabled, mini-app errors will be automatically sent to AI for fixing without showing a confirmation dialog.': '启用后，迷你应用错误会自动发送给 AI 修复，无需确认弹窗。',
    'When paid subscribers use your mini-apps, you earn': '当付费订阅用户使用你的迷你应用时，你将获得',
    'While anyone can create and use mini-apps for personal use,': '虽然任何人都可以创建并自用迷你应用，',
    'Yes, build my mini-app': '是的，帮我创建迷你应用',
    'Your Mini-Apps': '你的迷你应用',
    'Your display name': '你的显示名称',
    'Your password has been successfully changed. Redirecting you to your dashboard...': '你的密码已成功修改，正在跳转到控制台...',
    'Your profile URL:': '你的资料页链接：',
    'Your subscription is now active. Enjoy your new features!': '你的订阅现已生效，尽情使用新功能吧！',
    'and make $$ when other users use it': '当其他用户使用时还能赚取收益',
    'of the attributed revenue. We keep 30% to cover infrastructure, AI costs, and platform maintenance.': '的归因收入。我们保留 30% 用于基础设施、AI 成本和平台维护。',
    'publishing more than one public mini-app and earning revenue requires a paid subscription': '发布多个公开迷你应用并获得收益需要付费订阅',
    'you@example.com': 'you@example.com',
    'your_username': 'your_username',
    'sk-ant-...': 'sk-ant-...',
    'sk-...': 'sk-...',
    'Username must be at least 4 characters': '用户名至少需要 4 个字符',
    'Username must be 15 characters or less': '用户名最多 15 个字符',
    'Only letters, numbers, and underscores allowed': '仅允许字母、数字和下划线',
    'Username cannot start with underscore': '用户名不能以下划线开头',
    'Username cannot be only numbers': '用户名不能仅包含数字',
    'Invalid API key format. Anthropic API keys should start with "sk-ant-"': 'API 密钥格式无效。Anthropic 密钥应以 "sk-ant-" 开头',
    'Invalid username': '用户名无效',
    'Username is required to make your profile public': '公开个人资料需要先设置用户名',
    'This username is already taken': '该用户名已被占用',
    "Don't have an account?": '还没有账户？',
    "Don't see a mini-app that you are looking for?": '没有找到你要的迷你应用？',
    "Enter your email and we'll send you a reset link": '输入你的邮箱，我们会发送重置链接',
    "We've sent a password reset link to": '我们已将密码重置链接发送到',
    "Didn't receive the email? Check your spam folder or try again.": '没收到邮件？请检查垃圾邮箱或重试。',
    "Explain verbally what your mini-app will have to do and our AI will build it for you. You'll be able to test, refine, and publish it.": '用自然语言描述你的迷你应用需要做什么，我们的 AI 会为你构建。你可以测试、优化并发布它。',
    "Earn 70% of subscription revenue based on your apps' usage.": '根据你的应用使用量，你可获得 70% 的订阅收入分成。',
    "You'll be redirected to Stripe to complete setup": '你将被重定向到 Stripe 完成设置',
    "You're using the coding AI with the Max plan. No API key required.": '你正在使用 Max 套餐的编程 AI，无需 API 密钥。',
    "Our AI understands your requirements and writes all the code automatically. You can test it, refine it through conversation, and iterate until it's perfect.": '我们的 AI 能理解你的需求并自动编写全部代码。你可以测试、通过对话优化，并持续迭代到满意为止。',
    "Publish your mini-app for the world to use. When paid subscribers use your creation, you earn a share of the revenue. It's that simple.": '将你的迷你应用发布给全世界使用。当付费订阅用户使用你的作品时，你将获得收益分成。就是这么简单。',
    "Google Sign-in is blocked by this app's built-in browser.": '该应用内置浏览器阻止了 Google 登录。',
    "Google Sign-up is blocked by this app's built-in browser.": '该应用内置浏览器阻止了 Google 注册。',
    "To sign in with Google, you must open this page in your phone's main browser (Safari or Chrome).": '要使用 Google 登录，你必须在手机主浏览器（Safari 或 Chrome）中打开此页面。',
    "To sign up with Google, you must open this page in your phone's main browser (Safari or Chrome).": '要使用 Google 注册，你必须在手机主浏览器（Safari 或 Chrome）中打开此页面。',
  },
  ru: {
    'Language': 'Язык',
    'English': 'Английский',
    'French': 'Французский',
    'Chinese': 'Китайский',
    'Russian': 'Русский',
    'Switch to dark mode': 'Переключить на тёмную тему',
    'Switch to light mode': 'Переключить на светлую тему',
    'Back': 'Назад',
    'Dashboard': 'Панель',
    'Sign In': 'Войти',
    'Sign out': 'Выйти',
    'Settings': 'Настройки',
    'Earnings': 'Доход',
    'Admin': 'Админ',
    'Toggle menu': 'Открыть/закрыть меню',
    'Browse Public Apps': 'Смотреть публичные приложения',
    'Admin Dashboard': 'Админ-панель',
    'Terms': 'Условия',
    'Privacy': 'Конфиденциальность',
    'Contact': 'Контакты',
    'Documentation': 'Документация',
    'Creators': 'Для создателей',
    'Pricing': 'Тарифы',
    'Copyright': 'Авторские права',
    'Build your own mini-app': 'Создайте своё мини-приложение',
    'Powered by Llambada': 'Работает на Llambada',
    'About': 'О проекте',
    'Account': 'Аккаунт',
    'Account created!': 'Аккаунт создан!',
    'All features, highest limits': 'Все функции, максимальные лимиты',
    'All mini-apps': 'Все мини-приложения',
    'Allow others to see your profile page and public mini-apps': 'Разрешить другим видеть ваш профиль и публичные мини-приложения',
    'Already have an account?': 'Уже есть аккаунт?',
    'Anthropic API Key': 'Ключ API Anthropic',
    'Are you sure you want to delete this mini-app? This action cannot be undone and all conversations will be permanently lost.': 'Удалить это мини-приложение? Это действие необратимо, и все диалоги будут удалены навсегда.',
    'Auto-submit errors to AI': 'Автоматически отправлять ошибки в AI',
    'Back to sign in': 'Назад ко входу',
    'Basic features': 'Базовые функции',
    'Become a Creator': 'Стать создателем',
    'Bring Your Own API Key': 'Использовать свой API-ключ',
    'Browse Mini-Apps': 'Смотреть мини-приложения',
    'Browser Not Supported': 'Браузер не поддерживается',
    'Build Your Mini-App': 'Создайте своё мини-приложение',
    'Build and test mini-apps for free': 'Создавайте и тестируйте мини-приложения бесплатно',
    'Cancel': 'Отмена',
    'Check back later for new game mini-apps.': 'Зайдите позже, чтобы увидеть новые игровые мини-приложения.',
    'Check back later for new mini-apps.': 'Зайдите позже, чтобы увидеть новые мини-приложения.',
    'Check back later for new utility mini-apps.': 'Зайдите позже, чтобы увидеть новые утилиты.',
    'Check your Stripe dashboard to complete any required verification steps': 'Проверьте панель Stripe, чтобы завершить обязательную верификацию',
    'Check your email': 'Проверьте почту',
    'Choose a new password for your account': 'Выберите новый пароль для аккаунта',
    'Click on Settings (gear icon)': 'Нажмите «Настройки» (иконка шестерёнки)',
    'Confirm new password': 'Подтвердите новый пароль',
    'Connect Stripe for Payouts': 'Подключить Stripe для выплат',
    'Connect your bank account to receive payouts when users run your mini-apps.': 'Подключите банковский счёт, чтобы получать выплаты, когда пользователи запускают ваши мини-приложения.',
    'Connecting to Google...': 'Подключение к Google...',
    'Continue with Google': 'Продолжить с Google',
    'Copied!': 'Скопировано!',
    'Copy Page Link': 'Скопировать ссылку на страницу',
    'Country': 'Страна',
    'Country is set during registration and cannot be changed': 'Страна задаётся при регистрации и не может быть изменена',
    'Create Free Account': 'Создать бесплатный аккаунт',
    'Create account': 'Создать аккаунт',
    'Create an account': 'Создать аккаунт',
    'Create and manage your LLM mini-apps': 'Создавайте и управляйте своими LLM мини-приложениями',
    'Create mini-app': 'Создать мини-приложение',
    'Create new mini-app': 'Создать новое мини-приложение',
    'Create one': 'Создать',
    'Create unlimited private mini-apps and publish one public mini-app (non-monetized)': 'Создавайте неограниченно приватные мини-приложения и публикуйте одно публичное (без монетизации)',
    'Create your first mini-app': 'Создайте своё первое мини-приложение',
    'Create your first mini-app to start having conversations with LLMs and organizing your work.': 'Создайте первое мини-приложение, чтобы начать работать с LLM и организовать свой процесс.',
    'Creating...': 'Создание...',
    'Creator Monetization': 'Монетизация создателя',
    'Delete': 'Удалить',
    'Delete mini-app': 'Удалить мини-приложение',
    'Display name': 'Отображаемое имя',
    'Email address': 'Эл. почта',
    'Enter your Anthropic API key': 'Введите ключ API Anthropic',
    'Enter your Anthropic API key to use the coding AI. Get your key from': 'Введите ключ API Anthropic для использования кодового AI. Получить ключ можно на',
    'Enter your Kimi API key to use the coding AI. Get your key from': 'Введите ключ API Kimi для использования кодового AI. Получить ключ можно на',
    'Every creator gets limited free access to our code-writing AI. Perfect for getting started, experimenting with ideas, and building your first mini-apps.': 'Каждый создатель получает ограниченный бесплатный доступ к нашему AI для написания кода. Отлично подходит для старта, экспериментов и первых мини-приложений.',
    'Explore and use mini-apps created by our community': 'Изучайте и используйте мини-приложения от сообщества',
    'Failed to create conversation': 'Не удалось создать диалог',
    'Failed to create mini-app': 'Не удалось создать мини-приложение',
    'Failed to load mini-apps': 'Не удалось загрузить мини-приложения',
    'Flexible AI Access': 'Гибкий доступ к AI',
    'Forgot password?': 'Забыли пароль?',
    'Fork': 'Форк',
    'Free AI Credits': 'Бесплатные AI-кредиты',
    'Free accounts are limited to 1 mini-app.': 'Бесплатные аккаунты ограничены 1 мини-приложением.',
    'Free users:': 'Бесплатные пользователи:',
    'Full features, no ads': 'Полный функционал, без рекламы',
    'Games': 'Игры',
    'Generating title...': 'Генерация названия...',
    'Get Started Free': 'Начать бесплатно',
    'Go to Dashboard': 'Перейти в панель',
    'Go to Sign In': 'Перейти ко входу',
    'Go to your Dashboard': 'Перейдите в свою панель',
    'Got it': 'Понятно',
    'How It Works': 'Как это работает',
    'How to add your API key:': 'Как добавить API-ключ:',
    'Invalid or expired link': 'Недействительная или просроченная ссылка',
    'John Doe': 'Иван Иванов',
    'Join creators who are building useful tools and earning money - all without writing a single line of code.': 'Присоединяйтесь к создателям, которые делают полезные инструменты и зарабатывают, не написав ни строчки кода вручную.',
    'Kimi API Key': 'Ключ API Kimi',
    'GLM API Key': 'Ключ API GLM',
    'Enter your Z.ai GLM API key to use GLM coding models. Get your key from': 'Введите ваш ключ API Z.ai GLM для использования моделей кодирования GLM. Получите ключ на',
    'Let our AI build this app for you': 'Пусть наш AI создаст это приложение для вас',
    'Llambada - Create an account': 'Llambada - Создать аккаунт',
    'Llambada - Sign in': 'Llambada - Вход',
    'AI-Powered Mini-App Builder': 'Конструктор мини-приложений на базе ИИ',
    'Llambada provides AI capabilities to help you build, with options that fit your needs.': 'Llambada даёт AI-возможности для разработки с вариантами под ваши задачи.',
    'Manage': 'Управлять',
    'Max Plan Active': 'План Max активен',
    'Maybe Later': 'Позже',
    'Mini-app name': 'Название мини-приложения',
    'Must be at least 6 characters': 'Минимум 6 символов',
    'My awesome mini-app': 'Моё крутое мини-приложение',
    'New Mini-App': 'Новое мини-приложение',
    'New password': 'Новый пароль',
    'Nickname': 'Никнейм',
    'No coding experience required. Just describe what you want your mini-app to do, and our AI will build it for you. Share it with the world and earn money.': 'Опыт программирования не нужен. Просто опишите, что должно делать мини-приложение, и наш AI соберёт его для вас. Публикуйте и зарабатывайте.',
    'No credit card required to start': 'Для старта не нужна банковская карта',
    'No mini-apps found matching your search': 'По вашему запросу мини-приложения не найдены',
    'No mini-apps yet': 'Пока нет мини-приложений',
    'No public games yet': 'Пока нет публичных игровых мини-приложений',
    'No public mini-apps yet': 'Пока нет публичных мини-приложений',
    'No public utilities yet': 'Пока нет публичных утилит',
    'Or copy link manually': 'Или скопируйте ссылку вручную',
    'Or sign in with email': 'Или войдите через e-mail',
    'Or sign up with email': 'Или зарегистрируйтесь через e-mail',
    'Paid subscribers:': 'Платные подписчики:',
    'Password': 'Пароль',
    'Password must be at least 6 characters': 'Пароль должен быть не менее 6 символов',
    'Password updated!': 'Пароль обновлён!',
    'Passwords do not match': 'Пароли не совпадают',
    'Payouts Enabled': 'Выплаты включены',
    'Payouts are processed monthly via Stripe Connect. Track your earnings in real-time from your dashboard.': 'Выплаты обрабатываются ежемесячно через Stripe Connect. Отслеживайте доход в реальном времени из панели.',
    'Please check your email to verify your account.': 'Проверьте почту, чтобы подтвердить аккаунт.',
    'Please select your country': 'Пожалуйста, выберите страну',
    'Power users can connect their own Anthropic API key for unlimited AI usage. You pay Anthropic directly and get full control over your AI costs.': 'Продвинутые пользователи могут подключить свой API-ключ Anthropic для неограниченного использования AI. Вы платите Anthropic напрямую и полностью контролируете расходы.',
    'Preferences': 'Предпочтения',
    'Press Enter to search': 'Нажмите Enter для поиска',
    'Private': 'Приватное',
    'Public Mini-Apps': 'Публичные мини-приложения',
    'Public profile': 'Публичный профиль',
    'Publish multiple mini-apps publicly and earn 70% revenue share': 'Публикуйте несколько мини-приложений и получайте 70% выручки',
    'Published': 'Опубликовано',
    'Publishing & Earning Requirements': 'Требования для публикации и дохода',
    'Ready to Build Your First Mini-App?': 'Готовы создать первое мини-приложение?',
    'Ready to build your new mini-app?': 'Готовы создать новое мини-приложение?',
    'Redirecting to login...': 'Переход на страницу входа...',
    'Refresh': 'Обновить',
    'Remove All Keys': 'Удалить все ключи',
    'Request new link': 'Запросить новую ссылку',
    'Reset your password': 'Сбросить пароль',
    'Save Settings': 'Сохранить настройки',
    'Save and start building unlimited': 'Сохраните и начинайте создавать без ограничений',
    'Search': 'Поиск',
    'Search failed': 'Поиск не удался',
    'Select your country': 'Выберите страну',
    'Send reset link': 'Отправить ссылку для сброса',
    'Set a username first to make your profile public': 'Сначала задайте имя пользователя, чтобы сделать профиль публичным',
    'Set new password': 'Установить новый пароль',
    'Settings saved successfully': 'Настройки успешно сохранены',
    'Sign in': 'Войти',
    'Sign in to continue to your projects': 'Войдите, чтобы продолжить работу с проектами',
    'Sign in with Email': 'Войти по e-mail',
    'Sign up with Google': 'Зарегистрироваться через Google',
    'Simply tell the AI what you want your mini-app to do. Describe the problem it should solve or the task it should help users accomplish. No technical jargon needed.': 'Просто скажите AI, что должно делать мини-приложение. Опишите проблему или задачу для пользователя. Никакого техжаргона не нужно.',
    'Standard features with ads': 'Стандартные функции с рекламой',
    'Start Building': 'Начать создавать',
    'Start building your LLM projects today': 'Начните создавать свои LLM-проекты уже сегодня',
    'Stripe Connected - Verification Pending': 'Stripe подключён - ожидается проверка',
    'Subscription': 'Подписка',
    'Talking to AI': 'Разговаривая с AI',
    'Tap the menu icon (•••) in the corner and select "Open in Browser".': 'Нажмите значок меню (•••) в углу и выберите «Открыть в браузере».',
    'This password reset link is invalid or has expired. Please request a new one.': 'Ссылка для сброса пароля недействительна или истекла. Запросите новую.',
    'Top Results': 'Лучшие результаты',
    'Try Again': 'Попробовать снова',
    'Try another email': 'Попробовать другой e-mail',
    'Type the app you are looking for, for example: "Fill and sign a PDF form"': 'Введите приложение, которое вы ищете, например: «Заполнить и подписать PDF-форму»',
    'Update password': 'Обновить пароль',
    'Upgrade': 'Обновить тариф',
    'Upgrade anytime for more usage': 'Обновите тариф в любой момент для большего лимита',
    'Upgrade to Create More Mini-Apps': 'Обновите тариф, чтобы создавать больше мини-приложений',
    'Upgrade to the Standard plan to create unlimited mini-apps and unlock more features.': 'Перейдите на Standard, чтобы создавать неограниченно мини-приложения и открыть больше возможностей.',
    'Used for payouts. Cannot be changed later.': 'Используется для выплат. Позже изменить нельзя.',
    'Username': 'Имя пользователя',
    'Utilities': 'Утилиты',
    'Validating reset link...': 'Проверка ссылки для сброса...',
    'View Plans': 'Посмотреть тарифы',
    'View Pricing Plans': 'Посмотреть тарифные планы',
    'View Stripe Dashboard': 'Открыть панель Stripe',
    'View public page': 'Открыть публичную страницу',
    'View your public profile': 'Посмотреть публичный профиль',
    'Want to build mini-apps like these?': 'Хотите создавать такие же мини-приложения?',
    'Welcome back': 'С возвращением',
    'Welcome to {tier}!': 'Добро пожаловать в {tier}!',
    '{count} credits added!': '{count} кредитов добавлено!',
    'Your credits have been added to your account.': 'Кредиты добавлены на ваш аккаунт.',
    'When enabled, mini-app errors will be automatically sent to AI for fixing without showing a confirmation dialog.': 'Если включено, ошибки мини-приложений будут автоматически отправляться AI для исправления без подтверждения.',
    'When paid subscribers use your mini-apps, you earn': 'Когда платные подписчики используют ваши мини-приложения, вы получаете',
    'While anyone can create and use mini-apps for personal use,': 'Хотя любой может создавать и использовать мини-приложения для себя,',
    'Yes, build my mini-app': 'Да, создать моё мини-приложение',
    'Your Mini-Apps': 'Ваши мини-приложения',
    'Your display name': 'Ваше отображаемое имя',
    'Your password has been successfully changed. Redirecting you to your dashboard...': 'Пароль успешно изменён. Перенаправляем в панель...',
    'Your profile URL:': 'Ссылка на профиль:',
    'Your subscription is now active. Enjoy your new features!': 'Подписка активна. Пользуйтесь новыми возможностями!',
    'and make $$ when other users use it': 'и зарабатывайте, когда другие пользователи будут им пользоваться',
    'of the attributed revenue. We keep 30% to cover infrastructure, AI costs, and platform maintenance.': 'от атрибутированной выручки. Мы удерживаем 30% на инфраструктуру, AI-расходы и поддержку платформы.',
    'publishing more than one public mini-app and earning revenue requires a paid subscription': 'публикация более одного публичного мини-приложения и получение дохода требуют платной подписки',
    'you@example.com': 'you@example.com',
    'your_username': 'your_username',
    'sk-ant-...': 'sk-ant-...',
    'sk-...': 'sk-...',
    'Username must be at least 4 characters': 'Имя пользователя должно содержать минимум 4 символа',
    'Username must be 15 characters or less': 'Имя пользователя должно быть не длиннее 15 символов',
    'Only letters, numbers, and underscores allowed': 'Допустимы только буквы, цифры и подчёркивания',
    'Username cannot start with underscore': 'Имя пользователя не может начинаться с подчёркивания',
    'Username cannot be only numbers': 'Имя пользователя не может состоять только из цифр',
    'Invalid API key format. Anthropic API keys should start with "sk-ant-"': 'Неверный формат API-ключа. Ключ Anthropic должен начинаться с "sk-ant-"',
    'Invalid username': 'Недопустимое имя пользователя',
    'Username is required to make your profile public': 'Чтобы сделать профиль публичным, нужно имя пользователя',
    'This username is already taken': 'Это имя пользователя уже занято',
    "Don't have an account?": 'Нет аккаунта?',
    "Don't see a mini-app that you are looking for?": 'Не нашли нужное мини-приложение?',
    "Enter your email and we'll send you a reset link": 'Введите e-mail, и мы отправим ссылку для сброса',
    "We've sent a password reset link to": 'Мы отправили ссылку для сброса пароля на',
    "Didn't receive the email? Check your spam folder or try again.": 'Не получили письмо? Проверьте папку спама или попробуйте снова.',
    "Explain verbally what your mini-app will have to do and our AI will build it for you. You'll be able to test, refine, and publish it.": 'Опишите словами, что должно делать ваше мини-приложение, и наш AI создаст его. Вы сможете протестировать, доработать и опубликовать результат.',
    "Earn 70% of subscription revenue based on your apps' usage.": 'Получайте 70% подписочной выручки в зависимости от использования ваших приложений.',
    "You'll be redirected to Stripe to complete setup": 'Вы будете перенаправлены в Stripe для завершения настройки',
    "You're using the coding AI with the Max plan. No API key required.": 'Вы используете AI для кода с планом Max. API-ключ не требуется.',
    "Our AI understands your requirements and writes all the code automatically. You can test it, refine it through conversation, and iterate until it's perfect.": 'Наш AI понимает требования и автоматически пишет код. Вы можете тестировать, уточнять через диалог и улучшать результат шаг за шагом.',
    "Publish your mini-app for the world to use. When paid subscribers use your creation, you earn a share of the revenue. It's that simple.": 'Опубликуйте своё мини-приложение для всех. Когда платные подписчики используют ваш проект, вы получаете долю дохода. Всё просто.',
    "Google Sign-in is blocked by this app's built-in browser.": 'Вход через Google заблокирован встроенным браузером этого приложения.',
    "Google Sign-up is blocked by this app's built-in browser.": 'Регистрация через Google заблокирована встроенным браузером этого приложения.',
    "To sign in with Google, you must open this page in your phone's main browser (Safari or Chrome).": 'Чтобы войти через Google, откройте эту страницу в основном браузере телефона (Safari или Chrome).',
    "To sign up with Google, you must open this page in your phone's main browser (Safari or Chrome).": 'Чтобы зарегистрироваться через Google, откройте эту страницу в основном браузере телефона (Safari или Chrome).',
  },
};

function normalizeLocale(value?: string | null): UiLocale {
  if (!value) return 'en';
  const normalized = value.toLowerCase();
  if (normalized.startsWith('fr')) return 'fr';
  if (normalized.startsWith('zh')) return 'zh';
  if (normalized.startsWith('ru')) return 'ru';
  return 'en';
}

function applyParams(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_match, key: string) => {
    const value = params[key];
    return value === undefined ? `{${key}}` : String(value);
  });
}

interface I18nContextValue {
  locale: UiLocale;
  setLocale: (locale: UiLocale) => void;
  tt: (englishText: string, params?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function getInitialLocale(): UiLocale {
  if (typeof window === 'undefined') return 'en';
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved) return normalizeLocale(saved);
  return normalizeLocale(window.navigator.language);
}

function getHtmlLang(locale: UiLocale): string {
  if (locale === 'zh') return 'zh-CN';
  if (locale === 'fr') return 'fr';
  if (locale === 'ru') return 'ru';
  return 'en';
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<UiLocale>(() => getInitialLocale());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, locale);
    document.documentElement.lang = getHtmlLang(locale);
  }, [locale]);

  const tt = useCallback((englishText: string, params?: Record<string, string | number>) => {
    if (locale === 'en') return applyParams(englishText, params);
    const normalized = englishText.replace(/\s+/g, ' ').trim();
    const translated = TRANSLATIONS[locale][englishText] || TRANSLATIONS[locale][normalized] || englishText;
    return applyParams(translated, params);
  }, [locale]);

  const value = useMemo<I18nContextValue>(() => ({
    locale,
    setLocale,
    tt,
  }), [locale, tt]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used inside I18nProvider');
  }
  return context;
}
